/**
 * useLandingSLKflowLandingUnitCircleLogic
 *
 * [shared]-entity logic hook.
 * Reads/writes the shared-entity store instead of a per-trait
 * reducer — see @almadar/ui/hooks/useSharedEntityStore.
 * Dispatch resolves transitions + effects fully client-side (no
 * server round trip).
 *
 * @generated from schema
 * @trait LandingSLKflowLandingUnitCircle
 */

import { useState, useCallback, useEffect, useRef } from 'react';
import { useSharedEntityStoreContext, useSharedEntitySnapshot, useEventBus, useUIEvents, claimDelivery } from '@almadar/ui/hooks';
import { emitCascadeEvent } from '@/hooks/useOrbitalBridge';
import type { EntityFieldWrite, EntityRow, EventPayload, EventPayloadValue, FieldValue } from '@almadar/core';
function getNestedValue(obj: EventPayload | EventPayloadValue | FieldValue | Record<string, FieldValue | undefined> | null | undefined, path: string): FieldValue | undefined {
  if (obj === null || obj === undefined || !path) return undefined;
  if (typeof obj !== 'object') return undefined;
  if (!path.includes('.') && !Array.isArray(obj)) {
    return (obj as Record<string, FieldValue | undefined>)[path];
  }
  let value: EventPayload | EventPayloadValue | FieldValue | Record<string, FieldValue | undefined> | null | undefined = obj;
  for (const part of path.split('.')) {
    if (value === null || value === undefined || typeof value !== 'object') return undefined;
    if (Array.isArray(value)) {
      if (!/^\d+$/.test(part)) return undefined;
      value = (value as readonly (EventPayloadValue | FieldValue | null | undefined)[])[Number(part)];
      continue;
    }
    value = (value as Record<string, FieldValue | undefined>)[part];
  }
  return value as FieldValue | undefined;
}
import { mergeEntityFrame } from '@almadar/core';
import type { BusEvent } from '@almadar/core';
import type { EventResponse, LandingSLMathUnitCircleState, LandingSLKflowLandingUnitCircleEventPayloadMap } from '@app/shared';

export interface LandingSLKflowLandingUnitCircleConfig {
  linkedEntity?: string;
  entityId?: string;
  routeParams?: Record<string, string | undefined>;
  initPayload?: EventPayload;
  height: number;
  matchMode: string;
  radius: number;
  sampleCount: number;
  width: number;
  xMax: number;
  xMin: number;
  yMax: number;
  yMin: number;
}

const DEFAULT_LANDING_SL_KFLOW_LANDING_UNIT_CIRCLE_CONFIG = {
  height: 400,
  matchMode: "",
  radius: 6,
  sampleCount: 37,
  width: 600,
  xMax: 9,
  xMin: -9,
  yMax: 7,
  yMin: -7,
} as LandingSLKflowLandingUnitCircleConfig;

type LandingSLKflowLandingUnitCircleSlot = 'main';
export interface LandingSLKflowLandingUnitCircleLogicReturn {
  state: string;
  data: Record<string, EntityRow[]>;
  lastPayload: EventPayload | undefined;
  lastEvent: string | null;
  slotWriter: Partial<Record<LandingSLKflowLandingUnitCircleSlot, string | null>>;
  loading: boolean;
  pendingEvent: string | null;
  pendingFrom: string | null;
  error: string | null;
  fields: Partial<LandingSLMathUnitCircleState>;
  dispatch: (event: string, payload?: EventPayload) => Promise<EventResponse>;
  actions: { [K in keyof LandingSLKflowLandingUnitCircleEventPayloadMap]: (payload?: LandingSLKflowLandingUnitCircleEventPayloadMap[K]) => Promise<EventResponse> };
  config: LandingSLKflowLandingUnitCircleConfig;
}

export function useLandingSLKflowLandingUnitCircleLogic(config?: Partial<LandingSLKflowLandingUnitCircleConfig>): LandingSLKflowLandingUnitCircleLogicReturn {
  const mergedConfig: LandingSLKflowLandingUnitCircleConfig = { ...DEFAULT_LANDING_SL_KFLOW_LANDING_UNIT_CIRCLE_CONFIG, ...(config ?? {}) };

  // [shared] entity store + coalesced tick driver
  const _sharedStore = useSharedEntityStoreContext();
  const _sharedEntityId = 'LandingSLMathUnitCircleState';
  _sharedStore.seed(_sharedEntityId, { 'active': false, 'running': false, 'steps': 0, 'elapsedMs': 0, 'done': false, 'theta': 0, 'thetaDegrees': 0, 'cosValue': 1, 'sinValue': 0, 'speed': 1 });
  const _sharedSnapshot = useSharedEntitySnapshot(_sharedStore, _sharedEntityId);

  const fields = _sharedSnapshot as Partial<LandingSLMathUnitCircleState>;
  const data: Record<string, EntityRow[]> = { 'LandingSLMathUnitCircleState': [_sharedSnapshot] };

  // Local per-trait machine state — NOT the shared entity's fields,
  // which always live in the shared store (read fresh on every
  // dispatch, never cached here).
  const [machineState, setMachineState] = useState<string>('idle');
  const [lastPayload, setLastPayload] = useState<EventPayload | undefined>(undefined);
  const [lastEvent, setLastEvent] = useState<string | null>(null);
  const [slotWriter, setSlotWriter] = useState<Partial<Record<LandingSLKflowLandingUnitCircleSlot, string | null>>>({});
  const [error, setError] = useState<string | null>(null);
  const machineStateRef = useRef(machineState);
  machineStateRef.current = machineState;

  const eventBus = useEventBus();

  // Actor model: sequential event queue (prevents cross-trait race conditions)
  const eventQueueRef = useRef<Array<{ event: string; payload?: EventPayload; settle?: () => void }>>([]);
  const processingRef = useRef(false);

  // Transpiled client-side effects (Phase 4)
  // Each (fromState, event) transition's client effects are compiled to TypeScript.
  const executeTransitionEffects = useCallback((fromState: string, event: string, liveData: Record<string, EntityRow[]>, payload?: EventPayload): { fields: Record<string, EventPayloadValue | undefined>; newState: string | undefined; emits: Array<() => void> } => {
    const _fields: Record<string, EventPayloadValue | undefined> = {};
    const _emits: Array<() => void> = [];
    let _newState: string | undefined = undefined;
    const payloadObj = payload;
    const entity: EntityRow[] = liveData['LandingSLMathUnitCircleState'] ?? [];
    let fields: Record<string, EventPayloadValue | undefined> = entity[0] ?? {};


    if (fromState === 'idle' && event === 'INIT' && (true)) {
      // idle → idle (on INIT)
        _fields['done'] = false;
        fields = { ...(fields ?? {}), 'done': _fields['done'] } as Record<string, EventPayloadValue | undefined>;
        {

        const px = (6 * Math.cos((0)));

        const py = (6 * Math.sin((0)));

        const circleSamples = (Array.from({ length: Math.ceil((37 - 0)) }, (_, i) => 0 + i)).map(((idx) => (() => { const a = (+(idx ?? 0) * (6.283185307179586 / (37 - 1))); return { x: (6 * Math.cos(Number(a ?? 0))), y: (6 * Math.sin(Number(a ?? 0))) }; })()));

        _fields['theta'] = 0;
        fields = { ...(fields ?? {}), 'theta': _fields['theta'] } as Record<string, EventPayloadValue | undefined>;
        _fields['thetaDegrees'] = 0;
        fields = { ...(fields ?? {}), 'thetaDegrees': _fields['thetaDegrees'] } as Record<string, EventPayloadValue | undefined>;
        _fields['cosValue'] = 1;
        fields = { ...(fields ?? {}), 'cosValue': _fields['cosValue'] } as Record<string, EventPayloadValue | undefined>;
        _fields['sinValue'] = 0;
        fields = { ...(fields ?? {}), 'sinValue': _fields['sinValue'] } as Record<string, EventPayloadValue | undefined>;
        _fields['curves'] = [{ label: "unit circle" as const, color: "ink" as const, samples: circleSamples }, { label: "cos" as const, color: "series-1" as const, dash: "dashed" as const, samples: [{ x: 0, y: 0 }, { x: px, y: 0 }] }, { label: "sin" as const, color: "series-2" as const, dash: "dashed" as const, samples: [{ x: px, y: 0 }, { x: px, y: py }] }];
        fields = { ...(fields ?? {}), 'curves': _fields['curves'] } as Record<string, EventPayloadValue | undefined>;
        _fields['points'] = [{ x: px, y: py, label: "P" as const, color: "highlight" as const, radius: 6 }];
        fields = { ...(fields ?? {}), 'points': _fields['points'] } as Record<string, EventPayloadValue | undefined>;
        _fields['narration'] = "θ = 0 rad — cosθ = 1.00, sinθ = 0.00";
        fields = { ...(fields ?? {}), 'narration': _fields['narration'] } as Record<string, EventPayloadValue | undefined>;
        }

        _emits.push(((_p) => () => { eventBus.emit('UNITCIRCLE_UPDATED', _p, { trait: 'LandingSLKflowLandingUnitCircle' }); emitCascadeEvent(eventBus, 'LandingSL', 'LandingSLKflowLandingUnitCircle', { event: 'UNITCIRCLE_UPDATED', payload: _p, source: { orbital: 'LandingSL', trait: 'LandingSLKflowLandingUnitCircle' } }); })({ curves: fields?.curves, points: fields?.points, theta: fields?.theta, narration: fields?.narration }));
      _newState = 'idle';
    } else if (fromState === 'idle' && event === 'MODE_SELECTED' && ((!true && !(payloadObj?.mode === "")))) {
      // idle → idle (on MODE_SELECTED)
      _newState = 'idle';
    } else if (fromState === 'idle' && event === 'MODE_SELECTED' && ((payloadObj?.mode === ""))) {
      // idle → idle (on MODE_SELECTED)
        _fields['done'] = false;
        fields = { ...(fields ?? {}), 'done': _fields['done'] } as Record<string, EventPayloadValue | undefined>;
        {

        const px = (6 * Math.cos((0)));

        const py = (6 * Math.sin((0)));

        const circleSamples = (Array.from({ length: Math.ceil((37 - 0)) }, (_, i) => 0 + i)).map(((idx) => (() => { const a = (+(idx ?? 0) * (6.283185307179586 / (37 - 1))); return { x: (6 * Math.cos(Number(a ?? 0))), y: (6 * Math.sin(Number(a ?? 0))) }; })()));

        _fields['theta'] = 0;
        fields = { ...(fields ?? {}), 'theta': _fields['theta'] } as Record<string, EventPayloadValue | undefined>;
        _fields['thetaDegrees'] = 0;
        fields = { ...(fields ?? {}), 'thetaDegrees': _fields['thetaDegrees'] } as Record<string, EventPayloadValue | undefined>;
        _fields['cosValue'] = 1;
        fields = { ...(fields ?? {}), 'cosValue': _fields['cosValue'] } as Record<string, EventPayloadValue | undefined>;
        _fields['sinValue'] = 0;
        fields = { ...(fields ?? {}), 'sinValue': _fields['sinValue'] } as Record<string, EventPayloadValue | undefined>;
        _fields['curves'] = [{ label: "unit circle" as const, color: "ink" as const, samples: circleSamples }, { label: "cos" as const, color: "series-1" as const, dash: "dashed" as const, samples: [{ x: 0, y: 0 }, { x: px, y: 0 }] }, { label: "sin" as const, color: "series-2" as const, dash: "dashed" as const, samples: [{ x: px, y: 0 }, { x: px, y: py }] }];
        fields = { ...(fields ?? {}), 'curves': _fields['curves'] } as Record<string, EventPayloadValue | undefined>;
        _fields['points'] = [{ x: px, y: py, label: "P" as const, color: "highlight" as const, radius: 6 }];
        fields = { ...(fields ?? {}), 'points': _fields['points'] } as Record<string, EventPayloadValue | undefined>;
        _fields['narration'] = "θ = 0 rad — cosθ = 1.00, sinθ = 0.00";
        fields = { ...(fields ?? {}), 'narration': _fields['narration'] } as Record<string, EventPayloadValue | undefined>;
        }

        _emits.push(((_p) => () => { eventBus.emit('UNITCIRCLE_UPDATED', _p, { trait: 'LandingSLKflowLandingUnitCircle' }); emitCascadeEvent(eventBus, 'LandingSL', 'LandingSLKflowLandingUnitCircle', { event: 'UNITCIRCLE_UPDATED', payload: _p, source: { orbital: 'LandingSL', trait: 'LandingSLKflowLandingUnitCircle' } }); })({ curves: fields?.curves, points: fields?.points, theta: fields?.theta, narration: fields?.narration }));
      _newState = 'idle';
    } else if (fromState === 'idle' && event === 'LEARN_STEP' && (((fields?.active === true) && !(fields?.done === true)))) {
      // idle → idle (on LEARN_STEP)
        {

        const step = 0.2617993877991494;

        const twoPi = 6.283185307179586;

        const newTheta = (((((+(fields?.theta ?? 0) + +(step ?? 0)))) % (Number(twoPi ?? 0))) + (Number(twoPi ?? 0))) % (Number(twoPi ?? 0));

        const degrees = (Math.round((((+(newTheta ?? 0) / +(twoPi ?? 0)) * 3600))) / 10);

        const px = (6 * Math.cos(Number(newTheta ?? 0)));

        const py = (6 * Math.sin(Number(newTheta ?? 0)));

        const cosVal = (Math.round(((Math.cos(Number(newTheta ?? 0)) * 100))) / 100);

        const sinVal = (Math.round(((Math.sin(Number(newTheta ?? 0)) * 100))) / 100);

        const circleSamples = (Array.from({ length: Math.ceil((37 - 0)) }, (_, i) => 0 + i)).map(((idx) => (() => { const a = (+(idx ?? 0) * (+(twoPi ?? 0) / (37 - 1))); return { x: (6 * Math.cos(Number(a ?? 0))), y: (6 * Math.sin(Number(a ?? 0))) }; })()));

        _fields['theta'] = newTheta;
        fields = { ...(fields ?? {}), 'theta': _fields['theta'] } as Record<string, EventPayloadValue | undefined>;
        _fields['thetaDegrees'] = degrees;
        fields = { ...(fields ?? {}), 'thetaDegrees': _fields['thetaDegrees'] } as Record<string, EventPayloadValue | undefined>;
        _fields['cosValue'] = cosVal;
        fields = { ...(fields ?? {}), 'cosValue': _fields['cosValue'] } as Record<string, EventPayloadValue | undefined>;
        _fields['sinValue'] = sinVal;
        fields = { ...(fields ?? {}), 'sinValue': _fields['sinValue'] } as Record<string, EventPayloadValue | undefined>;
        _fields['curves'] = [{ label: "unit circle" as const, color: "ink" as const, samples: circleSamples }, { label: "cos" as const, color: "series-1" as const, dash: "dashed" as const, samples: [{ x: 0, y: 0 }, { x: px, y: 0 }] }, { label: "sin" as const, color: "series-2" as const, dash: "dashed" as const, samples: [{ x: px, y: 0 }, { x: px, y: py }] }];
        fields = { ...(fields ?? {}), 'curves': _fields['curves'] } as Record<string, EventPayloadValue | undefined>;
        _fields['points'] = [{ x: px, y: py, label: "P" as const, color: "highlight" as const, radius: 6 }];
        fields = { ...(fields ?? {}), 'points': _fields['points'] } as Record<string, EventPayloadValue | undefined>;
        _fields['narration'] = ["θ = ", ["", degrees].join(''), "° — cosθ = ", ["", cosVal].join(''), ", sinθ = ", ["", sinVal].join('')].join('');
        fields = { ...(fields ?? {}), 'narration': _fields['narration'] } as Record<string, EventPayloadValue | undefined>;
        }

        _emits.push(((_p) => () => { eventBus.emit('UNITCIRCLE_UPDATED', _p, { trait: 'LandingSLKflowLandingUnitCircle' }); emitCascadeEvent(eventBus, 'LandingSL', 'LandingSLKflowLandingUnitCircle', { event: 'UNITCIRCLE_UPDATED', payload: _p, source: { orbital: 'LandingSL', trait: 'LandingSLKflowLandingUnitCircle' } }); })({ curves: fields?.curves, points: fields?.points, theta: fields?.theta, narration: fields?.narration }));
      _newState = 'idle';
    } else if (fromState === 'idle' && event === 'LEARN_RESET' && ((fields?.active === true))) {
      // idle → idle (on LEARN_RESET)
        _fields['done'] = false;
        fields = { ...(fields ?? {}), 'done': _fields['done'] } as Record<string, EventPayloadValue | undefined>;
        {

        const px = (6 * Math.cos((0)));

        const py = (6 * Math.sin((0)));

        const circleSamples = (Array.from({ length: Math.ceil((37 - 0)) }, (_, i) => 0 + i)).map(((idx) => (() => { const a = (+(idx ?? 0) * (6.283185307179586 / (37 - 1))); return { x: (6 * Math.cos(Number(a ?? 0))), y: (6 * Math.sin(Number(a ?? 0))) }; })()));

        _fields['theta'] = 0;
        fields = { ...(fields ?? {}), 'theta': _fields['theta'] } as Record<string, EventPayloadValue | undefined>;
        _fields['thetaDegrees'] = 0;
        fields = { ...(fields ?? {}), 'thetaDegrees': _fields['thetaDegrees'] } as Record<string, EventPayloadValue | undefined>;
        _fields['cosValue'] = 1;
        fields = { ...(fields ?? {}), 'cosValue': _fields['cosValue'] } as Record<string, EventPayloadValue | undefined>;
        _fields['sinValue'] = 0;
        fields = { ...(fields ?? {}), 'sinValue': _fields['sinValue'] } as Record<string, EventPayloadValue | undefined>;
        _fields['curves'] = [{ label: "unit circle" as const, color: "ink" as const, samples: circleSamples }, { label: "cos" as const, color: "series-1" as const, dash: "dashed" as const, samples: [{ x: 0, y: 0 }, { x: px, y: 0 }] }, { label: "sin" as const, color: "series-2" as const, dash: "dashed" as const, samples: [{ x: px, y: 0 }, { x: px, y: py }] }];
        fields = { ...(fields ?? {}), 'curves': _fields['curves'] } as Record<string, EventPayloadValue | undefined>;
        _fields['points'] = [{ x: px, y: py, label: "P" as const, color: "highlight" as const, radius: 6 }];
        fields = { ...(fields ?? {}), 'points': _fields['points'] } as Record<string, EventPayloadValue | undefined>;
        _fields['narration'] = "θ = 0 rad — cosθ = 1.00, sinθ = 0.00";
        fields = { ...(fields ?? {}), 'narration': _fields['narration'] } as Record<string, EventPayloadValue | undefined>;
        }

        _emits.push(((_p) => () => { eventBus.emit('UNITCIRCLE_UPDATED', _p, { trait: 'LandingSLKflowLandingUnitCircle' }); emitCascadeEvent(eventBus, 'LandingSL', 'LandingSLKflowLandingUnitCircle', { event: 'UNITCIRCLE_UPDATED', payload: _p, source: { orbital: 'LandingSL', trait: 'LandingSLKflowLandingUnitCircle' } }); })({ curves: fields?.curves, points: fields?.points, theta: fields?.theta, narration: fields?.narration }));
      _newState = 'idle';
    } else if (fromState === 'idle' && event === 'LEARN_UPDATED' && ((fields?.active === true))) {
      // idle → idle (on LEARN_UPDATED)
      _newState = 'idle';
    }
    return { fields: _fields, newState: _newState, emits: _emits };
  }, [eventBus]);

  // Per-transition render-ui presence map (codegen).
  const getRenderUiSlots = useCallback((fromState: string, event: string): { write: LandingSLKflowLandingUnitCircleSlot[]; clear: LandingSLKflowLandingUnitCircleSlot[] } => {
    switch (fromState) {
      case 'idle':
        switch (event) {
          case 'INIT': return { write: ['main'] as LandingSLKflowLandingUnitCircleSlot[], clear: [] as LandingSLKflowLandingUnitCircleSlot[] };
          case 'LEARN_RESET': return { write: ['main'] as LandingSLKflowLandingUnitCircleSlot[], clear: [] as LandingSLKflowLandingUnitCircleSlot[] };
          case 'LEARN_STEP': return { write: ['main'] as LandingSLKflowLandingUnitCircleSlot[], clear: [] as LandingSLKflowLandingUnitCircleSlot[] };
          case 'LEARN_UPDATED': return { write: ['main'] as LandingSLKflowLandingUnitCircleSlot[], clear: [] as LandingSLKflowLandingUnitCircleSlot[] };
          case 'MODE_SELECTED': return { write: ['main'] as LandingSLKflowLandingUnitCircleSlot[], clear: ['main'] as LandingSLKflowLandingUnitCircleSlot[] };
          default: return { write: [], clear: [] };
        }
      default:
        return { write: [], clear: [] };
    }
  }, []);

  /**
   * Dispatch an event against the shared entity, committing locally
   * first (the fields mirror IS the `[runtime]` row).
   * No server round trip — this trait declares `local`.
   */
  const dispatch = useCallback(async (event: string, payload?: EventPayload): Promise<EventResponse> => {
    const prevState = machineStateRef.current;
    const liveData: Record<string, EntityRow[]> = { 'LandingSLMathUnitCircleState': [_sharedStore.getSnapshot(_sharedEntityId)] };
    const result = executeTransitionEffects(prevState, event, liveData, payload);
    if (result.newState === undefined) {
      const errorMsg = `No transition defined for ${event} from ${prevState}`;
      setError(errorMsg);
      return { success: false, transitioned: false, states: {}, emittedEvents: [], error: errorMsg } as EventResponse;
    }
    const writes: EntityFieldWrite[] = Object.entries(result.fields)
      .filter(([, value]) => value !== undefined)
      .map(([field, value]) => ({ field, value: value as FieldValue }));
    if (writes.length > 0) {
      _sharedStore.commit(_sharedEntityId, mergeEntityFrame(_sharedStore.getSnapshot(_sharedEntityId), writes));
    }
    for (const _emit of result.emits) _emit();
    machineStateRef.current = result.newState;
    setMachineState(result.newState);
    const _renderSlots = getRenderUiSlots(prevState, event);
    if (_renderSlots.write.length > 0 || _renderSlots.clear.length > 0) {
      setLastPayload(payload);
      setLastEvent(event);
      setSlotWriter((prev) => ({
        ...prev,
        ...Object.fromEntries(_renderSlots.write.map((s) => [s, event] as const)),
        ...Object.fromEntries(_renderSlots.clear.map((s) => [s, null] as const)),
      }));
    }
    setError(null);
    return { success: true, transitioned: true, states: { 'LandingSLKflowLandingUnitCircle': result.newState }, emittedEvents: [] } as EventResponse;
  }, [executeTransitionEffects, getRenderUiSlots, _sharedStore, _sharedEntityId]);

  // Actor model: drain event queue one at a time
  const drainQueue = useCallback(async () => {
    if (processingRef.current) return;
    processingRef.current = true;
    try {
      while (eventQueueRef.current.length > 0) {
        const entry = eventQueueRef.current.shift()!;
        try { await dispatch(entry.event, entry.payload); } finally { entry.settle?.(); }
      }
    } finally {
      processingRef.current = false;
    }
  }, [dispatch]);

  // Actor model: enqueue event and start drain
  const enqueueEvent = useCallback((event: string, payload?: EventPayload) => {
    return new Promise<void>((settle) => {
    eventQueueRef.current.push({ event, payload, settle });
    void drainQueue();
    });
  }, [drainQueue]);

  // Action creators (typed per-event)
  const actions: { [K in keyof LandingSLKflowLandingUnitCircleEventPayloadMap]: (payload?: LandingSLKflowLandingUnitCircleEventPayloadMap[K]) => Promise<EventResponse> } = {
    'INIT': () => dispatch('INIT'),
    'MODE_SELECTED': (payload?: LandingSLKflowLandingUnitCircleEventPayloadMap['MODE_SELECTED']) => dispatch('MODE_SELECTED', payload),
    'LEARN_STEP': (payload?: LandingSLKflowLandingUnitCircleEventPayloadMap['LEARN_STEP']) => dispatch('LEARN_STEP', payload),
    'LEARN_RESET': () => dispatch('LEARN_RESET'),
    'LEARN_UPDATED': (payload?: LandingSLKflowLandingUnitCircleEventPayloadMap['LEARN_UPDATED']) => dispatch('LEARN_UPDATED', payload),
    'UNITCIRCLE_UPDATED': (payload?: LandingSLKflowLandingUnitCircleEventPayloadMap['UNITCIRCLE_UPDATED']) => dispatch('UNITCIRCLE_UPDATED', payload),
    'RESET': () => dispatch('RESET'),
    'STEP': () => dispatch('STEP'),
    'TOGGLE_RUN': () => dispatch('TOGGLE_RUN'),
  };

  useUIEvents(enqueueEvent, 'LandingSL.LandingSLKflowLandingUnitCircle', ['INIT', 'MODE_SELECTED', 'LEARN_STEP', 'LEARN_RESET', 'LEARN_UPDATED'] as const, eventBus);

  // Auto-init on mount
  useEffect(() => {
    const __rp = Object.fromEntries(Object.entries(config?.routeParams ?? {}).filter(([, v]) => v !== undefined));
    const __init = { ...__rp, ...(config?.initPayload ?? {}), ...(config?.entityId !== undefined ? { entityId: config.entityId } : {}) };
    dispatch('INIT', Object.keys(__init).length > 0 ? __init : undefined);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Cross-trait event listeners (gap #13: qualified bus keys)
  useEffect(() => {
    // Listen for 'LEARN_RESET' (from LandingSLKflowLandingUnitCircleClock) → trigger 'LEARN_RESET'
    const unsub_learn_reset_from_landingslkflowlandingunitcircleclock_to_learn_reset = eventBus.on('UI:LandingSL.LandingSLKflowLandingUnitCircleClock.LEARN_RESET', (event: BusEvent) => {
      if (!claimDelivery(event.source, 'LandingSL.LandingSLKflowLandingUnitCircle', 'LEARN_RESET')) return;
      enqueueEvent('LEARN_RESET', event.payload);
    });
    // Listen for 'LEARN_STEP' (from LandingSLKflowLandingUnitCircleClock) → trigger 'LEARN_STEP'
    const unsub_learn_step_from_landingslkflowlandingunitcircleclock_to_learn_step = eventBus.on('UI:LandingSL.LandingSLKflowLandingUnitCircleClock.LEARN_STEP', (event: BusEvent) => {
      if (!claimDelivery(event.source, 'LandingSL.LandingSLKflowLandingUnitCircle', 'LEARN_STEP')) return;
      enqueueEvent('LEARN_STEP', event.payload);
    });
    // Listen for 'LEARN_UPDATED' (from LandingSLKflowLandingUnitCircleClock) → trigger 'LEARN_UPDATED'
    const unsub_learn_updated_from_landingslkflowlandingunitcircleclock_to_learn_updated = eventBus.on('UI:LandingSL.LandingSLKflowLandingUnitCircleClock.LEARN_UPDATED', (event: BusEvent) => {
      if (!claimDelivery(event.source, 'LandingSL.LandingSLKflowLandingUnitCircle', 'LEARN_UPDATED')) return;
      enqueueEvent('LEARN_UPDATED', event.payload);
    });
    return () => {
      if (typeof unsub_learn_reset_from_landingslkflowlandingunitcircleclock_to_learn_reset === 'function') unsub_learn_reset_from_landingslkflowlandingunitcircleclock_to_learn_reset();
      if (typeof unsub_learn_step_from_landingslkflowlandingunitcircleclock_to_learn_step === 'function') unsub_learn_step_from_landingslkflowlandingunitcircleclock_to_learn_step();
      if (typeof unsub_learn_updated_from_landingslkflowlandingunitcircleclock_to_learn_updated === 'function') unsub_learn_updated_from_landingslkflowlandingunitcircleclock_to_learn_updated();
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [eventBus, dispatch]);

  return {
    state: machineState,
    data,
    lastPayload,
    lastEvent,
    slotWriter,
    loading: false,
    pendingEvent: null,
    pendingFrom: null,
    error,
    fields,
    dispatch,
    actions,
    config: mergedConfig,
  };
}
